import {
  PORTFOLIO_CATEGORY_NAME_MAX_LENGTH,
  type PortfolioOrderSnapshot,
} from '@repo/api-contracts';
import { useEffect, useRef, useState } from 'react';
import { usePortfolioCategoriesQuery } from '@/entities/portfolio';
import { movedOrder, useReorderDrag } from '@/shared/lib';
import { Button, ContentCard, EmptyState, Input } from '@/shared/ui';
import { useCreatePortfolioCategory, useReorderPortfolioCategories } from '../model';
import { PortfolioCategoryRow } from './portfolio-category-row';

export const PortfolioCategoryManager = () => {
  const query = usePortfolioCategoriesQuery();
  const creation = useCreatePortfolioCategory();
  const reorder = useReorderPortfolioCategories();
  const [name, setName] = useState('');
  const [draft, setDraft] = useState<PortfolioOrderSnapshot | undefined>(query.data?.orderSnapshot);
  const [dirty, setDirty] = useState(false);
  const snapshot = query.data?.orderSnapshot;
  const seenToken = useRef(snapshot?.token);
  const [latest, setLatest] = useState<PortfolioOrderSnapshot | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const busy = creation.isPending || reorder.isPending || refreshing;
  useEffect(() => {
    if (!snapshot || busy || (draft && seenToken.current === snapshot.token)) return;
    seenToken.current = snapshot.token;
    if (dirty) setLatest(snapshot);
    else {
      setDraft(snapshot);
      setLatest(null);
    }
  }, [draft, snapshot, dirty, busy]);
  const create = async () => {
    if (busy || !name.trim()) return;
    reorder.reset();
    setMessage(null);
    if (await creation.createCategory({ name: name.trim() })) {
      setName('');
      setMessage('카테고리를 추가했습니다.');
    }
  };
  const saveOrder = async () => {
    if (!draft || busy) return;
    creation.reset();
    setMessage(null);
    setRefreshError(null);
    const result = await reorder.reorderCategories({
      orderedIds: draft.orderedIds,
      expectedToken: draft.token,
    });
    if (result) {
      setDraft(result);
      setDirty(false);
      setLatest(null);
      setMessage('카테고리 순서를 저장했습니다.');
    }
  };
  const refresh = async () => {
    if (busy) return;
    setRefreshing(true);
    setRefreshError(null);
    setMessage(null);
    try {
      const result = await query.refetch();
      if (result.data) setLatest(result.data.orderSnapshot);
      else setRefreshError('카테고리를 불러오지 못했습니다.');
    } catch {
      setRefreshError('카테고리를 불러오지 못했습니다.');
    } finally {
      setRefreshing(false);
    }
  };
  const move = (id: string, direction: 'UP' | 'DOWN') => {
    if (!draft || busy) return;
    const from = draft.orderedIds.indexOf(id);
    const to = from + (direction === 'UP' ? -1 : 1);
    if (from < 0 || to < 0 || to >= draft.orderedIds.length) return;
    setDraft({ ...draft, orderedIds: movedOrder(draft.orderedIds, from, to) });
    setDirty(true);
    setMessage(null);
  };
  const categoryIds = draft?.orderedIds ?? [];
  const drag = useReorderDrag({
    scope: 'categories',
    axis: 'x',
    ids: categoryIds,
    disabled: busy,
    onMove: (from, to) => {
      if (!draft || busy) return;
      setDraft({ ...draft, orderedIds: movedOrder(draft.orderedIds, from, to) });
      setDirty(true);
      setMessage(null);
    },
  });
  // 편집 중 새 카테고리도 표시하되, 초안에는 최신 순서 명시적 적용 후 포함한다.
  const displayedIds = [
    ...categoryIds,
    ...(query.data?.items.filter((item) => !categoryIds.includes(item.id)).map((item) => item.id) ??
      []),
  ];
  return (
    <ContentCard
      aria-label="포트폴리오 카테고리 관리"
      title="카테고리 관리"
      description="손잡이를 끌어 탭 순서를 바꾸세요. 손잡이에 초점을 두고 방향키로도 이동할 수 있습니다."
    >
      <div className="space-y-4">
        <form
          className="flex flex-wrap gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void create();
          }}
        >
          <Input
            aria-label="새 카테고리 이름"
            placeholder="새 카테고리 이름"
            value={name}
            disabled={busy}
            onChange={(event) => setName(event.target.value)}
            className="w-52"
          />
          <Button
            type="submit"
            disabled={
              busy ||
              !name.trim() ||
              Array.from(name.trim()).length > PORTFOLIO_CATEGORY_NAME_MAX_LENGTH
            }
          >
            카테고리 추가
          </Button>
        </form>
        {creation.error || reorder.error || refreshError ? (
          <p role="alert" className="text-sm text-destructive">
            {creation.error ?? reorder.error ?? refreshError}
          </p>
        ) : null}
        {message ? (
          <p role="status" className="text-sm">
            {message}
          </p>
        ) : null}
        {query.isPending ? (
          <EmptyState>카테고리를 불러오는 중…</EmptyState>
        ) : query.isError ? (
          <div>
            <p role="alert">카테고리를 불러오지 못했습니다.</p>
            <Button type="button" variant="outline" onClick={() => void query.refetch()}>
              카테고리 다시 시도
            </Button>
          </div>
        ) : (
          <ol className="flex flex-wrap items-start gap-2 py-2">
            {displayedIds.map((id, index) => {
              const category = query.data?.items.find((item) => item.id === id);
              return category ? (
                <PortfolioCategoryRow
                  key={id}
                  category={category}
                  first={index === 0}
                  last={index === categoryIds.length - 1}
                  disabled={busy}
                  canMove={categoryIds.includes(id)}
                  onMove={(direction) => move(id, direction)}
                  handleProps={drag.handleProps(id)}
                  rowProps={drag.rowProps(id)}
                  dropEdge={
                    drag.target?.id === id ? (drag.target.after ? 'after' : 'before') : undefined
                  }
                />
              ) : null;
            })}
          </ol>
        )}
        {latest ? (
          <div className="space-y-2 rounded border p-3">
            <p>
              최신 순서: {latest.orderedIds.length}개 카테고리. 적용하면 작성 중인 순서를
              교체합니다.
            </p>
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => {
                setDraft(latest);
                setDirty(false);
                setLatest(null);
                reorder.reset();
                setMessage(null);
              }}
            >
              최신 카테고리 순서 적용
            </Button>
          </div>
        ) : null}
        <div className="flex gap-2">
          <Button
            type="button"
            disabled={!draft || busy || query.isError}
            onClick={() => void saveOrder()}
          >
            카테고리 순서 저장
          </Button>
          {reorder.error || refreshError ? (
            <Button type="button" variant="outline" disabled={busy} onClick={() => void refresh()}>
              최신 카테고리 순서 확인
            </Button>
          ) : null}
        </div>
      </div>
    </ContentCard>
  );
};
